import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  Row,
  Select,
  Switch,
  message
} from "antd";
import moment from "moment";

const { TextArea } = Input;
const Option = Select.Option;
const FormItem = Form.Item;

const getInitialState = (school) => ({
  name: school?.name || "",
  address: school?.address || "",
  city: school?.city || "",
  country: school?.country || "",
  primaryEmail: school?.primaryEmail || "",
  secondaryEmail: school?.secondaryEmail || "",
  primaryPhone: school?.primaryPhone || "",
  secondaryPhone: school?.secondaryPhone || "",
  membershipTier: school?.membershipTier || "Small",
  staffLimit: school?.staffLimit || 10,
  startDate: school?.startDate ? moment(school.startDate) : null,
  expiryDate: school?.expiryDate ? moment(school.expiryDate) : null,
  registrationStatus: school?.registrationStatus || "pending",
  isActive: school?.isActive !== undefined ? school.isActive : true,
  notes: school?.notes || ""
});

const SchoolForm = ({ visible, school, loading, onCancel, onSave }) => {
  const [formState, setFormState] = useState(getInitialState(school));

  useEffect(() => {
    setFormState(getInitialState(school));
  }, [school, visible]);

  const handleChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTierChange = (value) => {
    const limits = {
      Small: 10,
      Medium: 100,
      Large: 500
    };

    setFormState((prev) => ({
      ...prev,
      membershipTier: value,
      staffLimit: limits[value]
    }));
  };

  const handleSubmit = async () => {
    if (
      !formState.name ||
      !formState.address ||
      !formState.city ||
      !formState.country ||
      !formState.primaryEmail ||
      !formState.primaryPhone
    ) {
      message.error("Please fill in all required fields");
      return;
    }

    if (
      formState.startDate &&
      formState.expiryDate &&
      formState.expiryDate.isSameOrBefore(formState.startDate, "day")
    ) {
      message.error("Expiry date must be after start date");
      return;
    }

    await onSave({
      name: formState.name,
      address: formState.address,
      city: formState.city,
      country: formState.country,
      primaryEmail: formState.primaryEmail,
      secondaryEmail: formState.secondaryEmail,
      primaryPhone: formState.primaryPhone,
      secondaryPhone: formState.secondaryPhone,
      membershipTier: formState.membershipTier,
      staffLimit: Number(formState.staffLimit) || 0,
      startDate: formState.startDate ? formState.startDate.toISOString() : null,
      expiryDate: formState.expiryDate ? formState.expiryDate.toISOString() : null,
      registrationStatus: formState.registrationStatus,
      isActive: formState.isActive,
      notes: formState.notes
    });
  };

  const formItemLayout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 }
  };

  return (
    <Drawer
      title={school ? `Edit ${school.name}` : "Edit School"}
      placement="right"
      width={720}
      visible={visible}
      onClose={onCancel}
      destroyOnClose
    >
      <Row gutter={16}>
        <Col span={12}>
          <FormItem
            {...formItemLayout}
            label={<span><span style={{ color: "red" }}>*</span> Name</span>}
          >
            <Input
              value={formState.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </FormItem>
        </Col>
        <Col span={12}>
          <FormItem
            {...formItemLayout}
            label={<span><span style={{ color: "red" }}>*</span> City</span>}
          >
            <Input
              value={formState.city}
              onChange={(e) => handleChange("city", e.target.value)}
            />
          </FormItem>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <FormItem
            {...formItemLayout}
            label={<span><span style={{ color: "red" }}>*</span> Country</span>}
          >
            <Input
              value={formState.country}
              onChange={(e) => handleChange("country", e.target.value)}
            />
          </FormItem>
        </Col>
        <Col span={12}>
          <FormItem
            {...formItemLayout}
            label={<span><span style={{ color: "red" }}>*</span> Address</span>}
          >
            <Input
              value={formState.address}
              onChange={(e) => handleChange("address", e.target.value)}
            />
          </FormItem>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <FormItem
            {...formItemLayout}
            label={<span><span style={{ color: "red" }}>*</span> Primary Email</span>}
          >
            <Input
              value={formState.primaryEmail}
              onChange={(e) => handleChange("primaryEmail", e.target.value)}
            />
          </FormItem>
        </Col>
        <Col span={12}>
          <FormItem {...formItemLayout} label="Secondary Email">
            <Input
              value={formState.secondaryEmail}
              onChange={(e) => handleChange("secondaryEmail", e.target.value)}
            />
          </FormItem>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <FormItem
            {...formItemLayout}
            label={<span><span style={{ color: "red" }}>*</span> Primary Phone</span>}
          >
            <Input
              value={formState.primaryPhone}
              onChange={(e) => handleChange("primaryPhone", e.target.value)}
            />
          </FormItem>
        </Col>
        <Col span={12}>
          <FormItem {...formItemLayout} label="Secondary Phone">
            <Input
              value={formState.secondaryPhone}
              onChange={(e) => handleChange("secondaryPhone", e.target.value)}
            />
          </FormItem>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <FormItem {...formItemLayout} label="Membership Tier">
            <Select
              value={formState.membershipTier}
              onChange={handleTierChange}
            >
              <Option value="Small">Small</Option>
              <Option value="Medium">Medium</Option>
              <Option value="Large">Large</Option>
            </Select>
          </FormItem>
        </Col>
        <Col span={8}>
          <FormItem {...formItemLayout} label="Staff Limit">
            <Input
              type="number"
              value={formState.staffLimit}
              onChange={(e) => handleChange("staffLimit", e.target.value)}
            />
          </FormItem>
        </Col>
        <Col span={8}>
          <FormItem {...formItemLayout} label="Registration Status">
            <Select
              value={formState.registrationStatus}
              onChange={(value) => handleChange("registrationStatus", value)}
            >
              <Option value="pending">Pending</Option>
              <Option value="approved">Approved</Option>
              <Option value="rejected">Rejected</Option>
            </Select>
          </FormItem>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <FormItem {...formItemLayout} label="Start Date">
            <DatePicker
              style={{ width: "100%" }}
              value={formState.startDate}
              onChange={(value) => handleChange("startDate", value)}
            />
          </FormItem>
        </Col>
        <Col span={12}>
          <FormItem {...formItemLayout} label="Expiry Date">
            <DatePicker
              style={{ width: "100%" }}
              value={formState.expiryDate}
              onChange={(value) => handleChange("expiryDate", value)}
            />
          </FormItem>
        </Col>
      </Row>

      <FormItem {...formItemLayout} label="Notes">
        <TextArea
          rows={4}
          value={formState.notes}
          onChange={(e) => handleChange("notes", e.target.value)}
        />
      </FormItem>

      <FormItem {...formItemLayout} label="Active">
        <Switch
          checked={formState.isActive}
          onChange={(checked) => handleChange("isActive", checked)}
        />
      </FormItem>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" loading={loading} onClick={handleSubmit}>
          Save
        </Button>
      </div>
    </Drawer>
  );
};

export default SchoolForm;
